// First-time discoverability hint for the stage/phase indicator's queued-freeze dots:
// while an ACCOUNT has never clicked one (any table, ever), the dots blink yellow for 2s
// at the entry to every Turn stage — but only once the hero has been seated at the table
// for 5 minutes. The first click, anywhere, retires the hint forever.
//
// Every rule here is biased AGAINST flashing (a hint that fails to appear is a shrug; a
// hint that keeps appearing after it was dismissed is an annoyance):
//  - the server profile flag (user_profiles.freeze_queue_clicked) is the authority, and
//    the gate flashes only when it has EXPLICITLY arrived as false — unknown never flashes;
//  - a localStorage belt is written the moment a dot is clicked, so a refresh/reconnect
//    that races the server write (or a snapshot that lags it) still can't revive the hint
//    on this browser;
//  - the 5-minute seat clock restarts on a refresh/table reopen (it is in-memory in
//    GameTable), which only ever DELAYS the flash.

export const FREEZE_HINT_FLASH_MS = 2000;
export const FREEZE_HINT_MIN_SEATED_MS = 5 * 60 * 1000;

const STORAGE_KEY = 'pup:freezeHintClicked';

// Browser-local "already clicked" belt. On a storage failure we claim CLICKED —
// unreadable state must suppress the hint, never show it.
export function freezeHintClickedLocally() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    return true;
  }
}

export function markFreezeHintClicked() {
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch (e) {
    // Ignore: the redux latch + server flag still suppress the hint this session.
  }
}

// The flash gate, evaluated at the moment the table ENTERS the Turn stage.
//  - serverClicked: gameSlice.freezeQueueClicked (true | false | null-unknown)
//  - mySeat: the hero's seat, null while spectating
//  - seatedAtMs: when the hero's seat appeared this mount (null = not seated)
export function shouldFlashFreezeHint({ serverClicked, mySeat, seatedAtMs, now }) {
  if (serverClicked !== false) return false; // unknown or already clicked → never flash
  if (freezeHintClickedLocally()) return false;
  if (mySeat == null) return false;
  if (seatedAtMs == null || now - seatedAtMs < FREEZE_HINT_MIN_SEATED_MS) return false;
  return true;
}
